import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'anchvwap',
  name: 'Anchored VWAP',
  shortName: 'AVWAP',
  category: 'volume',
  overlay: true,
  params: [
    {
      key: 'anchorType',
      label: 'Anchor',
      type: 'select',
      default: 'week',
      options: [
        { value: 'session', label: 'Session (Day)' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
      ],
    },
  ],
  lines: [{ key: 'avwap', label: 'AVWAP', color: '#ff6f00', width: 1, style: 'dashed' }],
}

/** ISO week number (weeks anchored near Monday). */
function isoWeek(tSec: number): number {
  // Add 4 days (epoch = Thursday) to align weeks to Monday starts
  return Math.floor((tSec + 4 * 86400) / (7 * 86400))
}

class AnchoredVWAP extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const anchorType = String(params['anchorType'] ?? 'week')
    const values: (number | null)[] = new Array<number | null>(candles.length).fill(null)

    let cumTPV = 0
    let cumV = 0
    let currentPeriod = -1

    const getPeriod = (t: number): number => {
      switch (anchorType) {
        case 'session':
          return Math.floor(t / 86400)
        case 'month': {
          const d = new Date(t * 1000)
          return d.getUTCFullYear() * 12 + d.getUTCMonth()
        }
        case 'week':
        default:
          return isoWeek(t)
      }
    }

    for (let i = 0; i < candles.length; i++) {
      const period = getPeriod(candles[i].t)
      if (period !== currentPeriod) {
        cumTPV = 0
        cumV = 0
        currentPeriod = period
      }
      const tp = (candles[i].h + candles[i].l + candles[i].c) / 3
      cumTPV += tp * candles[i].v
      cumV += candles[i].v
      values[i] = cumV > 0 ? cumTPV / cumV : null
    }

    return { lines: [{ key: 'avwap', values }] }
  }
}

registerIndicator(AnchoredVWAP, CONFIG)
